import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  Clipboard,
  Eye,
  EyeOff,
  KeyRound,
  Link2,
  LoaderCircle,
  RefreshCw,
  Save,
  ServerCog,
  ShieldCheck,
  Webhook,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { Skeleton } from '../../components/ui/skeleton'
import { useToast } from '../../components/ui/toast'
import { api } from '../../lib/api'
import { relativeTime } from '../../lib/format'
import { DeliverySettingsPanel } from './delivery-settings'
import { AutoReplySettingsPanel } from './auto-reply-settings'
import { FreeScoutSettingsPanel } from './freescout-settings'

interface FormState {
  apiBaseUrl: string
  apiKey: string
  wabaId: string
  phoneNumberId: string
  webhookVerifyToken: string
  webhookPathSecret: string
  requestTimeoutMs: number
}

const initialForm: FormState = {
  apiBaseUrl: 'https://api.dualhook.com/v25.0',
  apiKey: '',
  wabaId: '',
  phoneNumberId: '',
  webhookVerifyToken: '',
  webhookPathSecret: '',
  requestTimeoutMs: 15_000,
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-2 text-sm font-semibold">
      {label}
      {children}
      {hint && (
        <span className="text-xs font-normal leading-5 text-[var(--muted-foreground)]">{hint}</span>
      )}
    </label>
  )
}

function ChecklistItem({ ok, label }: { ok: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2.5 text-sm">
      {ok ? (
        <CheckCircle2 className="size-4.5 shrink-0 text-[var(--success)]" />
      ) : (
        <XCircle className="size-4.5 shrink-0 text-[var(--muted-foreground)]" />
      )}
      <span className={ok ? 'text-[var(--foreground)]' : 'text-[var(--muted-foreground)]'}>
        {label}
      </span>
    </li>
  )
}

export function SettingsPage() {
  const [form, setForm] = useState<FormState>(initialForm)
  const [showApiKey, setShowApiKey] = useState(false)
  const [showTokens, setShowTokens] = useState(false)
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const settings = useQuery({ queryKey: ['dualhook-settings'], queryFn: api.dualhookSettings })

  useEffect(() => {
    if (!settings.data) return
    setForm({
      apiBaseUrl: settings.data.apiBaseUrl,
      apiKey: '',
      wabaId: settings.data.wabaId,
      phoneNumberId: settings.data.phoneNumberId,
      webhookVerifyToken: settings.data.webhookVerifyToken,
      webhookPathSecret: settings.data.webhookPathSecret,
      requestTimeoutMs: settings.data.requestTimeoutMs,
    })
  }, [settings.data])

  const generate = useMutation({
    mutationFn: api.generateWebhookTokens,
    onSuccess: (tokens) => {
      setForm((current) => ({ ...current, ...tokens }))
      setShowTokens(true)
      showToast('Nuovi token generati. Salva la configurazione per applicarli.')
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Generazione fallita', 'error'),
  })
  const save = useMutation({
    mutationFn: () =>
      api.saveDualhookSettings({
        ...form,
        ...(form.apiKey.trim() ? { apiKey: form.apiKey.trim() } : {}),
      }),
    onSuccess: () => {
      const connectionComplete = Boolean(
        (form.apiKey.trim() || settings.data?.apiKeyConfigured) &&
        form.wabaId.trim() &&
        form.phoneNumberId.trim(),
      )
      setForm((current) => ({ ...current, apiKey: '' }))
      showToast(
        connectionComplete
          ? 'Configurazione Dualhook salvata'
          : 'Webhook salvato. Ora puoi completare il collegamento in Dualhook.',
      )
      void queryClient.invalidateQueries({ queryKey: ['dualhook-settings'] })
      void queryClient.invalidateQueries({ queryKey: ['system-status'] })
    },
    onError: (error) =>
      showToast(error instanceof Error ? error.message : 'Salvataggio fallito', 'error'),
  })
  const test = useMutation({
    mutationFn: api.testDualhook,
    onSuccess: (status) => {
      showToast(
        status.connected
          ? 'Connessione Dualhook verificata'
          : status.error || 'Connessione non riuscita',
        status.connected ? 'success' : 'error',
      )
      void queryClient.invalidateQueries({ queryKey: ['dualhook-settings'] })
      void queryClient.invalidateQueries({ queryKey: ['system-status'] })
    },
    onError: (error) => showToast(error instanceof Error ? error.message : 'Test fallito', 'error'),
  })

  const webhookUrl = useMemo(() => {
    if (!form.webhookPathSecret) return ''
    const origin = window.location.origin
    return `${origin}/webhooks/dualhook/${form.webhookPathSecret}`
  }, [form.webhookPathSecret])

  const copy = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value)
    showToast(`${label} copiato`)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (form.apiKey.trim() && !form.apiKey.trim().startsWith('dh_live_')) {
      showToast(
        'La API key deve essere la Outbound API key Dualhook e iniziare con dh_live_',
        'error',
      )
      return
    }
    save.mutate()
  }

  const webhookReady = Boolean(form.webhookVerifyToken && form.webhookPathSecret)
  const connectionReady = Boolean(
    settings.data?.apiKeyConfigured && form.wabaId.trim() && form.phoneNumberId.trim(),
  )

  if (settings.isLoading) {
    return (
      <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-10">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-3 h-4 w-96 max-w-full" />
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    )
  }

  return (
    <section className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-10">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[var(--primary)]">
            Impostazioni
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight">Connessione Dualhook</h1>
          <p className="mt-2 max-w-2xl text-base leading-7 text-[var(--muted-foreground)]">
            Prima prepara il webhook, poi completa la connessione con i dati restituiti da Dualhook.
          </p>
        </div>
        <Badge
          className={
            settings.data?.connection.connected
              ? 'bg-[var(--success-soft)] text-[var(--success)]'
              : 'bg-[var(--warning-soft)] text-[var(--warning)]'
          }
        >
          <span
            className={`mr-2 size-2 rounded-full ${settings.data?.connection.connected ? 'bg-[var(--success)]' : 'bg-[var(--warning)]'}`}
          />
          {settings.data?.connection.connected ? 'Connesso' : 'Da verificare'}
        </Badge>
      </header>

      <div className="mt-6 grid gap-3 md:grid-cols-2">
        <div className="flex gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--primary)] text-sm font-bold text-[var(--primary-foreground)]">
            1
          </span>
          <div>
            <p className="font-semibold">Prepara il webhook</p>
            <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
              Genera i token, salva e usa Callback URL e Verify token nel primo passaggio Dualhook.
              WABA e Phone ID possono restare vuoti.
            </p>
          </div>
        </div>
        <div className="flex gap-3 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-card)]">
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[var(--primary-soft)] text-sm font-bold text-[var(--primary)]">
            2
          </span>
          <div>
            <p className="font-semibold">Completa la connessione</p>
            <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
              Dopo il collegamento inserisci WABA ID, Phone Number ID e la Outbound API key
              <span className="font-mono"> dh_live_…</span> della stessa connessione.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-8 grid items-start gap-5 lg:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.75fr)]">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <Card className="order-2 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                <KeyRound className="size-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">2. Completa la connessione API</h2>
                <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
                  La chiave viene cifrata e non sarà più mostrata integralmente.
                </p>
              </div>
            </div>
            <div className="mt-6 grid gap-5">
              <Field label="API Base URL">
                <Input
                  value={form.apiBaseUrl}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, apiBaseUrl: event.target.value }))
                  }
                />
              </Field>
              <Field
                label="API key"
                hint={
                  settings.data?.apiKeyConfigured
                    ? 'Una chiave è già salvata. Lascia vuoto per mantenerla oppure incolla una nuova dh_live_… per sostituirla.'
                    : 'Da Dualhook: Connection → Overview → Outbound API key. Non usare la password PEMMY2 né un token Meta.'
                }
              >
                <div className="relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={form.apiKey}
                    placeholder={settings.data?.apiKeyConfigured ? '••••••••••••••••' : 'dh_live_…'}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, apiKey: event.target.value }))
                    }
                    className="pr-12"
                  />
                  <button
                    type="button"
                    aria-label={showApiKey ? 'Nascondi API key' : 'Mostra API key'}
                    className="absolute right-1 top-1 grid size-9 place-items-center rounded-lg text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                    onClick={() => setShowApiKey((value) => !value)}
                  >
                    {showApiKey ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </button>
                </div>
              </Field>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="WABA ID">
                  <Input
                    value={form.wabaId}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, wabaId: event.target.value }))
                    }
                    placeholder="123456789…"
                  />
                </Field>
                <Field label="Phone Number ID">
                  <Input
                    value={form.phoneNumberId}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, phoneNumberId: event.target.value }))
                    }
                    placeholder="123456789…"
                  />
                </Field>
              </div>
              <Field label="Timeout richieste" hint="Valore consigliato: 15 secondi.">
                <div className="relative">
                  <Input
                    type="number"
                    min={1000}
                    max={60000}
                    step={1000}
                    value={form.requestTimeoutMs}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        requestTimeoutMs: Number(event.target.value),
                      }))
                    }
                    className="pr-12"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-[var(--muted-foreground)]">
                    ms
                  </span>
                </div>
              </Field>
            </div>
          </Card>

          <Card className="order-1 p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="grid size-10 shrink-0 place-items-center rounded-xl bg-[var(--primary-soft)] text-[var(--primary)]">
                  <Webhook className="size-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">1. Prepara il webhook</h2>
                  <p className="mt-1 text-sm leading-6 text-[var(--muted-foreground)]">
                    Usa questi valori nella Webhook Override di Dualhook.
                  </p>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => generate.mutate()}
                disabled={generate.isPending}
              >
                <RefreshCw className={`size-4 ${generate.isPending ? 'animate-spin' : ''}`} />{' '}
                Genera token
              </Button>
            </div>
            <div className="mt-6 grid gap-5">
              <Field label="Verify token">
                <div className="flex gap-2">
                  <Input
                    type={showTokens ? 'text' : 'password'}
                    value={form.webhookVerifyToken}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, webhookVerifyToken: event.target.value }))
                    }
                  />
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label="Copia verify token"
                    disabled={!form.webhookVerifyToken}
                    onClick={() => void copy(form.webhookVerifyToken, 'Verify token')}
                  >
                    <Clipboard className="size-4" />
                  </Button>
                </div>
              </Field>
              <Field label="Path secret">
                <div className="flex gap-2">
                  <Input
                    type={showTokens ? 'text' : 'password'}
                    value={form.webhookPathSecret}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, webhookPathSecret: event.target.value }))
                    }
                  />
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label="Mostra o nascondi token"
                    onClick={() => setShowTokens((value) => !value)}
                  >
                    {showTokens ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                </div>
              </Field>
              <Field
                label="Callback URL"
                hint="Copia l’URL dopo aver salvato eventuali nuovi token."
              >
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={webhookUrl}
                    placeholder="Genera il path secret"
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label="Copia callback URL"
                    disabled={!webhookUrl}
                    onClick={() => void copy(webhookUrl, 'Callback URL')}
                  >
                    <Clipboard className="size-4" />
                  </Button>
                </div>
              </Field>
            </div>
          </Card>

          <div className="order-3 flex flex-col-reverse gap-2 rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-3 shadow-[var(--shadow-card)] sm:flex-row sm:justify-end sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
            <Button
              variant="secondary"
              disabled={!connectionReady || test.isPending || save.isPending}
              onClick={() => test.mutate()}
            >
              {test.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Link2 className="size-4" />
              )}{' '}
              Verifica connessione
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? (
                <LoaderCircle className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}{' '}
              {connectionReady || form.apiKey.trim() ? 'Salva configurazione' : 'Salva webhook'}
            </Button>
          </div>
        </form>

        <aside className="flex flex-col gap-5 lg:sticky lg:top-8">
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="grid size-10 place-items-center rounded-xl bg-[var(--success-soft)] text-[var(--success)]">
                <ShieldCheck className="size-5" />
              </div>
              <div>
                <h2 className="font-semibold">Pronto per il test</h2>
                <p className="text-xs text-[var(--muted-foreground)]">Controlli essenziali</p>
              </div>
            </div>
            <ul className="mt-5 flex flex-col gap-3.5">
              <ChecklistItem
                ok={Boolean(settings.data?.apiKeyConfigured)}
                label="API key salvata"
              />
              <ChecklistItem
                ok={Boolean(form.wabaId && form.phoneNumberId)}
                label="Identificativi WhatsApp presenti"
              />
              <ChecklistItem ok={webhookReady} label="Token webhook presenti" />
              <ChecklistItem
                ok={Boolean(settings.data?.connection.connected)}
                label="API Dualhook verificata"
              />
              <ChecklistItem
                ok={Boolean(settings.data?.lastWebhookAt)}
                label="Primo webhook ricevuto"
              />
            </ul>
          </Card>

          <Card className="p-5">
            <div className="flex items-center gap-3">
              <ServerCog className="size-5 text-[var(--primary)]" />
              <h2 className="font-semibold">Stato connessione</h2>
            </div>
            <dl className="mt-5 grid gap-4 text-sm">
              <div>
                <dt className="text-[var(--muted-foreground)]">Numero</dt>
                <dd className="mt-1 font-semibold">
                  {settings.data?.connection.displayPhoneNumber ?? 'Non verificato'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--muted-foreground)]">Nome verificato</dt>
                <dd className="mt-1 font-semibold">
                  {settings.data?.connection.verifiedName ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--muted-foreground)]">Qualità</dt>
                <dd className="mt-1 font-semibold">
                  {settings.data?.connection.qualityRating ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--muted-foreground)]">Ultimo controllo</dt>
                <dd className="mt-1 font-semibold">
                  {relativeTime(settings.data?.connection.checkedAt)}
                </dd>
              </div>
              <div>
                <dt className="text-[var(--muted-foreground)]">Ultimo webhook</dt>
                <dd className="mt-1 font-semibold">{relativeTime(settings.data?.lastWebhookAt)}</dd>
              </div>
            </dl>
            {settings.data?.connection.error && (
              <div className="mt-5 rounded-xl border border-[var(--destructive-border)] bg-[var(--destructive-soft)] p-3 text-sm leading-5 text-[var(--destructive)]">
                {settings.data.connection.error}
              </div>
            )}
          </Card>
        </aside>
      </div>
      <AutoReplySettingsPanel />
      <FreeScoutSettingsPanel />
      <DeliverySettingsPanel />
    </section>
  )
}
